// Analytics & Anomalies — a network-wide read-out across everything OpsPanther
// knows: forecast, actuals/accuracy, sortation capacity, package mix, hub events
// and H2H flow. The centerpiece is an anomaly feed that surfaces what needs a look
// (over-capacity, MHE breaches, customer volume spikes/drops, weak accuracy, stale
// actuals, active events) ranked by severity.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Card, PageHeader, Spinner } from "../../components/ui";
import { BarsChart, HBarList } from "../../components/charts";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDate, fmtN, startOfWeekSat, today } from "../../lib/format";
import type { Branch, Customer, Hub, HubEvent } from "../../lib/types";
import { forecastCustomerHub, forecastHubDay, meanAbsPctError } from "../../lib/forecast/engine";
import { makeTransitFn, receivedInByDay } from "../../lib/forecast/transit";
import { flowShares, shareOf } from "../../lib/forecast/flowShares";
import { listCustomers } from "../customers/customersApi";
import { listHubs } from "../hubs/hubsApi";
import { listBranches } from "../branches/branchesApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "../forecast/overridesApi";
import { listAccountCustomerActuals, type CustomerActual } from "../actuals/customerActualsApi";
import { getFlowShareTotals, type BranchFlow } from "../actuals/branchFlowApi";

const PAST_WEEKS = 8;
const FUTURE_WEEKS = 4;
const ACC_WEEKS = 4; // accuracy window (completed weeks)
const weekDatesFrom = (start: string) => Array.from({ length: 7 }, (_, d) => addDays(start, d));

type Sev = "bad" | "warn" | "info";
interface Anomaly {
  sev: Sev;
  cat: string;
  title: string;
  detail: string;
}

const SEV_RANK: Record<Sev, number> = { bad: 0, warn: 1, info: 2 };
const SEV_STYLE: Record<Sev, string> = {
  bad: "border-red-200 bg-red-50 text-red-700",
  warn: "border-amber-200 bg-amber-50 text-amber-700",
  info: "border-sky-200 bg-sky-50 text-sky-700",
};
const SEV_DOT: Record<Sev, string> = { bad: "🔴", warn: "🟠", info: "🔵" };

export default function AnalyticsPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);
  const [flow, setFlow] = useState<BranchFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const nowWeek = useMemo(() => startOfWeekSat(today()), []);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listBranches(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listAccountCustomerActuals(accountId, addDays(nowWeek, -(PAST_WEEKS + 3) * 7), addDays(nowWeek, -1)),
      getFlowShareTotals(accountId, addDays(nowWeek, -28), addDays(nowWeek, -1)),
    ])
      .then(([c, h, b, ov, ev, ac, fl]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setBranches(b.filter((x) => x.active));
        setOverrides(ov);
        setEvents(ev);
        setActuals(ac);
        setFlow(fl);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load analytics.")))
      .finally(() => setLoading(false));
  }, [accountId, nowWeek]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const shares = useMemo(() => flowShares(flow), [flow]);

  // Actuals-based analysis anchors to the last COMPLETE week (so a partially-
  // imported recent week doesn't read as a network-wide volume drop).
  const maxActualDate = useMemo(() => actuals.reduce((m, a) => (a.date > m ? a.date : m), ""), [actuals]);
  const lastCompleteWeek = useMemo(() => {
    if (!maxActualDate) return addDays(nowWeek, -7);
    let ws = startOfWeekSat(maxActualDate);
    if (addDays(ws, 6) > maxActualDate) ws = addDays(ws, -7); // current week is partial → use the prior full week
    return ws;
  }, [maxActualDate, nowWeek]);
  const hubName = useMemo(() => {
    const m = new Map(hubs.map((h) => [h.id, h.code || h.name]));
    return (id: string) => m.get(id) ?? "?";
  }, [hubs]);

  const futureWeeks = useMemo(
    () => Array.from({ length: FUTURE_WEEKS }, (_, k) => addDays(nowWeek, k * 7)),
    [nowWeek]
  );
  const futureDates = useMemo(() => futureWeeks.flatMap((w) => weekDatesFrom(w)), [futureWeeks]);
  const pastWeeks = useMemo(
    () => Array.from({ length: PAST_WEEKS }, (_, k) => addDays(lastCompleteWeek, (k - (PAST_WEEKS - 1)) * 7)),
    [lastCompleteWeek]
  );

  // own(hub, date) — cached forecast.
  const ownDay = useMemo(() => {
    const cache = new Map<string, number>();
    return (h: string, d: string) => {
      const k = `${h}|${d}`;
      let v = cache.get(k);
      if (v === undefined) {
        v = forecastHubDay(customers, h, d, overrideMap, events);
        cache.set(k, v);
      }
      return v;
    };
  }, [customers, overrideMap, events]);

  // Actuals indexed by hub|date and customer-week totals.
  const actualByHubDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of actuals) m.set(`${a.hubId}|${a.date}`, (m.get(`${a.hubId}|${a.date}`) ?? 0) + a.actualVolume);
    return m;
  }, [actuals]);
  const custWeekActual = useMemo(() => {
    // customerId → number[PAST_WEEKS] of weekly actual totals
    const m = new Map<string, number[]>();
    const weekIndex = new Map(pastWeeks.map((w, i) => [w, i]));
    for (const a of actuals) {
      const wk = startOfWeekSat(a.date);
      const idx = weekIndex.get(wk);
      if (idx === undefined) continue;
      let arr = m.get(a.customerId);
      if (!arr) {
        arr = new Array(PAST_WEEKS).fill(0);
        m.set(a.customerId, arr);
      }
      arr[idx] += a.actualVolume;
    }
    return m;
  }, [actuals, pastWeeks]);

  // Network forecast for the next 4 weeks + WoW.
  const fourWkForecast = useMemo(() => futureDates.reduce((s, d) => s + hubs.reduce((a, h) => a + ownDay(h.id, d), 0), 0), [futureDates, hubs, ownDay]);
  const wowPct = useMemo(() => {
    const w0 = futureWeeks[0] ? weekDatesFrom(futureWeeks[0]).reduce((s, d) => s + hubs.reduce((a, h) => a + ownDay(h.id, d), 0), 0) : 0;
    const prev = weekDatesFrom(addDays(nowWeek, -7)).reduce((s, d) => s + hubs.reduce((a, h) => a + ownDay(h.id, d), 0), 0);
    return prev > 0 ? ((w0 - prev) / prev) * 100 : null;
  }, [futureWeeks, nowWeek, hubs, ownDay]);

  // Trailing accuracy (last ACC_WEEKS complete weeks), network + per hub.
  const accWindow = useMemo(() => {
    const dates: string[] = [];
    for (let k = ACC_WEEKS - 1; k >= 0; k--) dates.push(...weekDatesFrom(addDays(lastCompleteWeek, -k * 7)));
    return dates;
  }, [lastCompleteWeek]);
  const hubAccuracy = useMemo(() => {
    return hubs
      .map((h) => {
        const rows = accWindow
          .map((d) => ({ forecast: ownDay(h.id, d), actual: actualByHubDate.get(`${h.id}|${d}`) ?? 0 }))
          .filter((r) => r.actual > 0);
        const mape = meanAbsPctError(rows);
        return { h, mape, acc: mape == null ? null : Math.max(0, 100 - mape), n: rows.length };
      })
      .filter((x) => x.n > 0);
  }, [hubs, accWindow, ownDay, actualByHubDate]);
  const networkAccuracy = useMemo(() => {
    const rows = hubs.flatMap((h) => accWindow.map((d) => ({ forecast: ownDay(h.id, d), actual: actualByHubDate.get(`${h.id}|${d}`) ?? 0 }))).filter((r) => r.actual > 0);
    const mape = meanAbsPctError(rows);
    return mape == null ? null : Math.max(0, 100 - mape);
  }, [hubs, accWindow, ownDay, actualByHubDate]);

  // Sortation capacity (next 4 weeks): own + received-in peak day vs hub capacity.
  const overCapHubs = useMemo(() => {
    const allHubIds = hubs.map((h) => h.id);
    const tFn = makeTransitFn(hubs, branches, false); // same-day for the capacity scan
    const recv = receivedInByDay({ hubIds: allHubIds, branches, dates: futureDates, own: ownDay, share: (o, b) => shareOf(shares, o, b), transit: (o, b) => tFn(o, b) });
    return hubs
      .filter((h) => h.capacity && h.capacity > 0)
      .map((h) => {
        let peak = 0;
        let peakDate = "";
        for (const d of futureDates) {
          const sort = ownDay(h.id, d) + (recv.get(`${h.id}|${d}`) ?? 0);
          if (sort > peak) ((peak = sort), (peakDate = d));
        }
        const util = Math.round((peak / (h.capacity as number)) * 100);
        return { h, util, peak, peakDate };
      })
      .filter((x) => x.util > 100)
      .sort((a, b) => b.util - a.util);
  }, [hubs, branches, futureDates, ownDay, shares]);

  // Customer volume anomalies: latest complete week vs trailing baseline (z-score).
  const custAnomalies = useMemo(() => {
    const out: Array<{ c: Customer; latest: number; mean: number; z: number; pct: number }> = [];
    for (const c of customers) {
      if (!c.active) continue;
      const wk = custWeekActual.get(c.id);
      if (!wk) continue;
      const base = wk.slice(0, PAST_WEEKS - 1).filter((v) => v > 0);
      const latest = wk[PAST_WEEKS - 1];
      if (base.length < 3 || latest <= 0) continue;
      const mean = base.reduce((s, v) => s + v, 0) / base.length;
      const sd = Math.sqrt(base.reduce((s, v) => s + (v - mean) ** 2, 0) / base.length);
      if (mean <= 0) continue;
      const z = sd > 0 ? (latest - mean) / sd : 0;
      const pct = ((latest - mean) / mean) * 100;
      if (Math.abs(z) >= 2 && Math.abs(pct) >= 25) out.push({ c, latest, mean, z, pct });
    }
    return out.sort((a, b) => Math.abs(b.z) - Math.abs(a.z));
  }, [customers, custWeekActual]);

  // Stale actuals: active, onboarded, but no actuals in the last 2 complete weeks.
  const staleCustomers = useMemo(() => {
    const recentWeeks = new Set([lastCompleteWeek, addDays(lastCompleteWeek, -7)]);
    const hasRecent = new Set<string>();
    for (const a of actuals) if (recentWeeks.has(startOfWeekSat(a.date))) hasRecent.add(a.customerId);
    return customers.filter(
      (c) => c.active && c.tier !== 1 && (!c.onboardingDate || c.onboardingDate <= addDays(lastCompleteWeek, -14)) && !hasRecent.has(c.id) && (custWeekActual.get(c.id)?.some((v) => v > 0) ?? false)
    );
  }, [customers, actuals, lastCompleteWeek, custWeekActual]);

  const liveEvents = useMemo(() => events.filter((e) => e.active !== false && e.endDate >= addDays(nowWeek, 0)), [events, nowWeek]);

  // Build the ranked anomaly feed.
  const anomalies = useMemo(() => {
    const out: Anomaly[] = [];
    for (const x of overCapHubs)
      out.push({ sev: x.util > 115 ? "bad" : "warn", cat: "Capacity", title: `${hubName(x.h.id)} over capacity — peak ${x.util}%`, detail: `Peak sortation ${fmtN(x.peak)} vs cap ${fmtN(x.h.capacity ?? 0)} on ${fmtDate(x.peakDate)} (own + H2H received).` });
    for (const a of custAnomalies)
      out.push({ sev: Math.abs(a.z) >= 3 ? "bad" : "warn", cat: a.pct > 0 ? "Volume spike" : "Volume drop", title: `${a.c.name} ${a.pct > 0 ? "up" : "down"} ${Math.abs(a.pct).toFixed(0)}% last week`, detail: `Last week ${fmtN(a.latest)} vs ${fmtN(Math.round(a.mean))} avg (z=${a.z.toFixed(1)}).` });
    for (const x of hubAccuracy)
      if (x.acc != null && x.acc < 80) out.push({ sev: x.acc < 70 ? "bad" : "warn", cat: "Accuracy", title: `${hubName(x.h.id)} forecast accuracy ${x.acc.toFixed(0)}%`, detail: `Last ${ACC_WEEKS} weeks vs actuals (MAPE ${(x.mape ?? 0).toFixed(0)}%). Consider recalibrating.` });
    for (const c of staleCustomers.slice(0, 8)) out.push({ sev: "warn", cat: "Data", title: `${c.name} — no actuals in last 2 weeks`, detail: "Active customer with history but missing recent imports — accuracy & anomaly checks are blind here." });
    for (const e of liveEvents) out.push({ sev: "info", cat: "Event", title: `${hubName(e.hubId)} — ${e.type}`, detail: `${fmtDate(e.startDate)} → ${fmtDate(e.endDate)}${e.redistributeVolume ? " · reroutes volume" : ""}.` });
    return out.sort((a, b) => SEV_RANK[a.sev] - SEV_RANK[b.sev]);
  }, [overCapHubs, custAnomalies, hubAccuracy, staleCustomers, liveEvents, hubName]);

  // Network volume by week: 8 past actual + 4 future forecast (with trend line).
  const weeklySeries = useMemo(() => {
    const past = pastWeeks.map((w) => ({ label: fmtDate(addDays(w, 6)), value: Math.round(weekDatesFrom(w).reduce((s, d) => s + hubs.reduce((a, h) => a + (actualByHubDate.get(`${h.id}|${d}`) ?? 0), 0), 0)), color: "#94a3b8" }));
    const future = futureWeeks.map((w) => ({ label: fmtDate(addDays(w, 6)), value: Math.round(weekDatesFrom(w).reduce((s, d) => s + hubs.reduce((a, h) => a + ownDay(h.id, d), 0), 0)), color: "#0f766e" }));
    return [...past.filter((p) => p.value > 0), ...future];
  }, [pastWeeks, futureWeeks, hubs, actualByHubDate, ownDay]);

  // Top customers by 4-week forecast.
  const topCustomers = useMemo(() => {
    return customers
      .map((c) => ({ c, value: futureDates.reduce((s, d) => s + hubs.reduce((a, h) => a + forecastCustomerHub(c, h.id, d, overrideMap, events), 0), 0) }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [customers, futureDates, hubs, overrideMap, events]);

  // Biggest week-over-week movers (last complete week vs prior), by actual.
  const movers = useMemo(() => {
    const out: Array<{ c: Customer; cur: number; prev: number; pct: number }> = [];
    for (const c of customers) {
      const wk = custWeekActual.get(c.id);
      if (!wk) continue;
      const cur = wk[PAST_WEEKS - 1];
      const prev = wk[PAST_WEEKS - 2];
      if (prev <= 0 || cur <= 0) continue;
      out.push({ c, cur, prev, pct: ((cur - prev) / prev) * 100 });
    }
    return out.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct)).slice(0, 8);
  }, [customers, custWeekActual]);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }
  if (loading) return <Spinner />;

  const sevCount = (s: Sev) => anomalies.filter((a) => a.sev === s).length;

  return (
    <div className="space-y-4">
      <PageHeader title="Analytics & Anomalies" subtitle="Network-wide health across forecast, actuals, sortation capacity, events and flow — with the items that need a look ranked on top." />

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {/* KPI strip */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <Kpi label="4-week forecast" value={fmtN(fourWkForecast)} sub="next 4 weeks" />
        <Kpi label="WoW change" value={wowPct == null ? "—" : `${wowPct > 0 ? "+" : ""}${wowPct.toFixed(1)}%`} sub="W1 vs last week" tone={wowPct == null ? undefined : Math.abs(wowPct) > 15 ? "bad" : Math.abs(wowPct) > 7 ? "warn" : "good"} />
        <Kpi label="Forecast accuracy" value={networkAccuracy == null ? "—" : `${networkAccuracy.toFixed(0)}%`} sub={`last ${ACC_WEEKS} wks`} tone={networkAccuracy == null ? undefined : networkAccuracy >= 90 ? "good" : networkAccuracy >= 80 ? "warn" : "bad"} />
        <Kpi label="Active customers" value={fmtN(customers.filter((c) => c.active).length)} sub={`${hubs.length} hubs`} />
        <Kpi label="Hubs over capacity" value={fmtN(overCapHubs.length)} sub="next 4 wks · sortation" tone={overCapHubs.length ? "bad" : "good"} />
        <Kpi label="Anomalies flagged" value={fmtN(anomalies.length)} sub={`${sevCount("bad")} critical · ${sevCount("warn")} warn`} tone={sevCount("bad") ? "bad" : sevCount("warn") ? "warn" : "good"} />
      </div>

      {/* Anomaly feed */}
      <Card>
        <div className="mb-1 text-sm font-medium text-slate-700">Anomaly feed</div>
        <p className="mb-3 text-[11px] text-slate-400">What changed or needs attention, ranked by severity: capacity, volume spikes/drops (z-score on recent actuals), weak accuracy, missing data, and active events.</p>
        {anomalies.length === 0 ? (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">✓ All clear — no anomalies detected across capacity, volume, accuracy, data freshness or events.</p>
        ) : (
          <div className="space-y-1.5">
            {anomalies.map((a, i) => (
              <div key={i} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${SEV_STYLE[a.sev]}`}>
                <span>{SEV_DOT[a.sev]}</span>
                <div className="min-w-0">
                  <div className="font-medium">
                    <span className="mr-2 rounded bg-white/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{a.cat}</span>
                    {a.title}
                  </div>
                  <div className="text-[11px] opacity-80">{a.detail}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Network volume by week */}
      <Card>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-slate-700">Network volume by week</div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-slate-400" /> actual (past 8 wks)</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-teal-700" /> forecast (next 4 wks)</span>
          </div>
        </div>
        <BarsChart data={weeklySeries} showLine lineLabel="trend" />
      </Card>

      {/* Accuracy by hub + Top customers */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Forecast accuracy by hub · last {ACC_WEEKS} weeks</div>
          <p className="mb-3 text-[11px] text-slate-400">100% − MAPE. Lower bars = hubs to recalibrate.</p>
          {hubAccuracy.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No actuals yet to score accuracy.</p>
          ) : (
            <HBarList
              max={100}
              unit="%"
              items={[...hubAccuracy]
                .sort((a, b) => (a.acc ?? 0) - (b.acc ?? 0))
                .map((x) => ({ label: hubName(x.h.id), value: Math.round(x.acc ?? 0), tone: (x.acc ?? 0) >= 90 ? "ok" : (x.acc ?? 0) >= 80 ? "warn" : "bad" }))}
            />
          )}
        </Card>
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Top customers · next 4 weeks</div>
          <p className="mb-3 text-[11px] text-slate-400">Largest shippers by forecast volume.</p>
          <HBarList items={topCustomers.map((x) => ({ label: x.c.name, value: Math.round(x.value), color: "#0ea5e9" }))} />
        </Card>
      </div>

      {/* Biggest movers */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">Biggest week-over-week movers · actuals</div>
          <p className="mt-0.5 text-[11px] text-slate-400">Largest swings between the last two completed weeks.</p>
        </div>
        {movers.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-400">Not enough actuals to compare weeks yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 text-right font-medium">Prev week</th>
                <th className="px-3 py-2 text-right font-medium">Last week</th>
                <th className="px-5 py-2 text-right font-medium">Δ%</th>
              </tr>
            </thead>
            <tbody>
              {movers.map((m) => (
                <tr key={m.c.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-1.5 font-medium text-slate-800">{m.c.name}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtN(m.prev)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtN(m.cur)}</td>
                  <td className={`px-5 py-1.5 text-right tabular-nums font-medium ${m.pct > 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {m.pct > 0 ? "+" : ""}
                    {m.pct.toFixed(0)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "good" | "warn" | "bad" }) {
  const c = tone === "bad" ? "text-red-600" : tone === "warn" ? "text-amber-600" : tone === "good" ? "text-emerald-600" : "text-slate-900";
  const dot = tone === "bad" ? "bg-red-500" : tone === "warn" ? "bg-amber-500" : tone === "good" ? "bg-emerald-500" : "";
  return (
    <Card>
      <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        {dot && <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />}
        {label}
      </p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${c}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-slate-400">{sub}</p>}
    </Card>
  );
}
